import { cn, Rating } from "@au-schein-de/au-ui-library";
import type { JSX, RefObject } from "react";
import { RatingWithLaurel } from "../expert-review/rating-with-laurel.component";
import { useAdaptiveRatingGap } from "./use-adaptive-rating-gap.hook";

type ProductCardRatingRowProps = {
	size: "default" | "sm";
	rating: string | number;
	ratingCount: string;
	noRatingLabel: string;
	hasUserRating: boolean;
	showExpertRating: boolean;
	expertRating: number;
};

const expertLaurelClassName =
	"md:[--laurel-font-size:14px] md:[--laurel-line-height:19.6px] md:[--laurel-leaf-width:8px] md:[--laurel-leaf-height:16px]";

const noRatingTextClassName =
	"truncate text-gray-500 text-caption md:text-body-sm";

function ExpertRatedLaurel({
	expertRating,
	laurelRef,
}: {
	expertRating: number;
	laurelRef: RefObject<HTMLDivElement | null>;
}): JSX.Element {
	return (
		<div ref={laurelRef} className="shrink-0" data-testid="expert-rated-laurel">
			<RatingWithLaurel
				rating={expertRating}
				size="xs-mobile"
				className={expertLaurelClassName}
			/>
		</div>
	);
}

export function ProductCardRatingRow({
	size,
	rating,
	ratingCount,
	noRatingLabel,
	hasUserRating,
	showExpertRating,
	expertRating,
}: ProductCardRatingRowProps): JSX.Element {
	const measureKey = [
		size,
		rating,
		ratingCount,
		noRatingLabel,
		hasUserRating,
		expertRating,
	].join("|");
	const { containerRef, primaryRef, laurelRef, gapClass } =
		useAdaptiveRatingGap(showExpertRating, measureKey);

	const userRating =
		!hasUserRating && showExpertRating ? (
			<div className="min-w-0 shrink overflow-hidden">
				<div
					ref={primaryRef}
					data-testid="product-no-user-rating"
					className={noRatingTextClassName}
					title={noRatingLabel}
				>
					{noRatingLabel}
				</div>
			</div>
		) : (
			<div ref={primaryRef} className="shrink-0">
				<Rating
					size={size}
					rating={rating}
					ratingCount={ratingCount}
					noRatingText={noRatingLabel}
				/>
			</div>
		);

	if (showExpertRating) {
		return (
			<div
				className="mt-1 w-full min-w-0 md:mt-3"
				data-testid="product-card-rating-row"
			>
				<div
					ref={containerRef}
					data-rating-gap={gapClass === "gap-3" ? "spacious" : "compact"}
					className={cn("flex min-w-0 max-w-full items-center", gapClass)}
				>
					{userRating}
					<ExpertRatedLaurel
						expertRating={expertRating}
						laurelRef={laurelRef}
					/>
				</div>
			</div>
		);
	}

	return (
		<div
			className="mt-1 w-full min-w-0 md:mt-3"
			data-testid="product-card-rating-row"
		>
			{userRating}
		</div>
	);
}
